// Sending a web upload: one job per object, each with its own progress, so a
// failed file is retried alone and finished ones are never sent again.
//
// The session (lib/web-upload/publish.ts → shared/upload/publish-seed.ts) is
// opened once. Its presigned URLs last two hours and its pending seed a day,
// so a retry — even after the connection dropped for a while — reuses it:
// done jobs stay done; a failed one re-sends its object (one PUT each).
// Cancel aborts the pending seed. A reload loses the files themselves; there
// is no resuming across one. A failure before the session opened (the
// manifest POST) keeps the input, so `restart` sends the same files again.

import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { track } from "@web-shared/lib/analytics";
import { webTransport } from "@web-shared/lib/audiotool-upload";
import { ApiError } from "@web-shared/lib/api";
import {
  abortSeedUpload,
  completeSeedUpload,
  putUploadJob,
  type CompletedFile,
  type SeedUploadSession,
} from "@shared/upload/publish-seed.js";
import { openStemsUpload, UploadProblem, type PrepareStage, type StemsUploadInput } from "@/lib/web-upload/publish";

export type JobStatus = "queued" | "sending" | "sent" | "failed";

export interface JobView {
  id: string;
  kind: "master" | "clip" | "preview";
  /** Content hash, for clips: which tracks this job carries. */
  hash: string | null;
  bytes: number;
  sent: number;
  status: JobStatus;
}

export type UploadPhase =
  | { kind: "idle" }
  | { kind: "preparing"; stage: PrepareStage; done?: number; total?: number }
  | { kind: "sending" }
  | { kind: "finishing" }
  | { kind: "done"; seedId: string }
  | { kind: "failed"; message: string; reason: FailReason };

/** How many objects go up at once. */
const CONCURRENCY = 3;

export type FailReason = "too_big" | "unreadable" | "network" | "server" | "cancelled";

/** Whether sending the same files again could work: not for a file that is
 * too big or can't be read. */
export function canRetry(reason: FailReason): boolean {
  return reason === "network" || reason === "server";
}

function failReason(err: unknown): FailReason {
  if (err instanceof UploadProblem) return /couldn't be read/.test(err.message) ? "unreadable" : "too_big";
  if (err instanceof TypeError) return "network"; // fetch's "Failed to fetch"
  return "server";
}

function messageOf(err: unknown): string {
  if (err instanceof UploadProblem) return err.message;
  if (err instanceof TypeError) return "The connection dropped. Check your internet and try again.";
  // A 5xx body says "Internal error." — nothing a producer can act on. A 4xx
  // names the problem (NOTHING_TO_UPLOAD, a size limit), so it stays.
  if (err instanceof ApiError && err.status >= 500) return "Something went wrong on our side. Try again in a minute.";
  return err instanceof Error && err.message ? err.message : "Something went wrong on our side. Try again in a minute.";
}

export function useStemsUpload() {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<UploadPhase>({ kind: "idle" });
  const [jobs, setJobs] = useState<JobView[]>([]);
  const session = useRef<SeedUploadSession | null>(null);
  const completed = useRef(new Map<string, CompletedFile>());
  const cancelled = useRef(false);
  const tracks = useRef(0);
  const parent = useRef<string | null>(null);
  const lastInput = useRef<StemsUploadInput | null>(null);

  const patch = useCallback((id: string, change: Partial<JobView>) => {
    setJobs((all) => all.map((j) => (j.id === id ? { ...j, ...change } : j)));
  }, []);

  const fail = useCallback((err: unknown) => {
    const reason = failReason(err);
    track("web_upload_failed", { detail: reason });
    setPhase({ kind: "failed", message: messageOf(err), reason });
  }, []);

  /** Seal the seed once every job is in. */
  const finish = useCallback(async () => {
    const s = session.current;
    if (!s || cancelled.current) return;
    if (s.jobs.some((j) => !completed.current.has(j.id))) return;
    setPhase({ kind: "finishing" });
    try {
      const done = await completeSeedUpload(webTransport, s, s.jobs.map((j) => completed.current.get(j.id)!));
      if (cancelled.current) return;
      setPhase({ kind: "done", seedId: done.seedId });
      track("seed_created", { seedId: done.seedId, detail: "web_upload", value: tracks.current });
      if (parent.current) track("remix_completed", { seedId: done.seedId, detail: "web_upload" });
      void queryClient.invalidateQueries({ queryKey: ["web"] });
    } catch (err) {
      fail(err);
    }
  }, [fail, queryClient]);

  /** Send some jobs, a few at a time. Failures stay on their row. */
  const send = useCallback(async (ids: string[]) => {
    const s = session.current;
    if (!s) return;
    setPhase({ kind: "sending" });
    const queue = s.jobs.filter((j) => ids.includes(j.id));
    for (const j of queue) patch(j.id, { status: "queued" });
    const worker = async (): Promise<void> => {
      for (let job = queue.shift(); job; job = queue.shift()) {
        if (cancelled.current) return;
        patch(job.id, { status: "sending" });
        try {
          const id = job.id;
          const done = await putUploadJob(webTransport, job, (sent) => patch(id, { sent }));
          completed.current.set(job.id, done);
          patch(job.id, { status: "sent", sent: job.bytes.byteLength });
        } catch {
          patch(job.id, { status: "failed" });
        }
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    if (cancelled.current) return;
    if (s.jobs.every((j) => completed.current.has(j.id))) await finish();
    else track("web_upload_failed", { detail: "network" });
  }, [finish, patch]);

  const start = useCallback(async (input: StemsUploadInput) => {
    cancelled.current = false;
    lastInput.current = input;
    session.current = null;
    completed.current = new Map();
    tracks.current = input.stems.length;
    parent.current = input.parentSeedId;
    setJobs([]);
    setPhase({ kind: "preparing", stage: "converting", done: 0, total: input.stems.length });
    let s: SeedUploadSession;
    try {
      s = await openStemsUpload(webTransport, input, (stage, done, total) =>
        setPhase({ kind: "preparing", stage, ...(done !== undefined ? { done } : {}), ...(total !== undefined ? { total } : {}) }),
      );
    } catch (err) {
      if (!cancelled.current) fail(err);
      return;
    }
    if (cancelled.current) {
      await abortSeedUpload(webTransport, s);
      return;
    }
    session.current = s;
    setJobs(s.jobs.map((j) => ({
      id: j.id,
      kind: j.kind,
      hash: j.plan.hash ?? null,
      bytes: j.bytes.byteLength,
      sent: 0,
      status: "queued",
    })));
    if (s.jobs.length === 0) await finish(); // the pool had every byte already
    else await send(s.jobs.map((j) => j.id));
  }, [fail, finish, send]);

  /** Send the failed jobs again; anything already sent stays sent. */
  const retry = useCallback(async (ids?: string[]) => {
    const s = session.current;
    if (!s) return;
    const failed = s.jobs.filter((j) => !completed.current.has(j.id)).map((j) => j.id);
    await send(ids ? failed.filter((id) => ids.includes(id)) : failed);
  }, [send]);

  /** Open the upload again with the same files, after it failed before any was sent. */
  const restart = useCallback(async () => {
    if (lastInput.current) await start(lastInput.current);
  }, [start]);

  /** Retry sealing after /complete failed. */
  const retryFinish = useCallback(() => finish(), [finish]);

  const cancel = useCallback(async () => {
    cancelled.current = true;
    const s = session.current;
    session.current = null;
    setPhase({ kind: "idle" });
    setJobs([]);
    track("web_upload_failed", { detail: "cancelled" });
    if (s) await abortSeedUpload(webTransport, s);
  }, []);

  return { phase, jobs, session: session.current, start, restart, retry, retryFinish, cancel };
}
